import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogActions,
  DialogContent, DialogTitle, Grid, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import GavelIcon from '@mui/icons-material/Gavel';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { useApiQuery, useApiMutation } from '@/hooks/useApiQuery';
import { useToast } from '@/components/ToastProvider';
import { regOps } from '@/api/endpoints';
import { formatDate } from '@/utils/format';
import type { MedicalJurisdiction } from '@/types/medical';

type RegSource = {
  id: number;
  jurisdiction: number;
  source_type: 'statute' | 'regulation' | 'guideline' | 'case_law' | 'standard';
  title: string;
  url: string;
  document: string | null;
  effective_from: string;
  effective_to: string | null;
  source_hash: string;
  notes: string;
};

const SOURCE_TYPES = [
  { code: 'statute',    label: 'Statute' },
  { code: 'regulation', label: 'Regulation' },
  { code: 'guideline',  label: 'Guideline' },
  { code: 'case_law',   label: 'Case law' },
  { code: 'standard',   label: 'Standard' },
];

export function RegOpsSourcesPage() {
  const [params] = useSearchParams();
  const filterJurisdictionId = params.get('jurisdiction');

  const sourcesQ = useApiQuery({
    queryKey: ['regops', 'sources'],
    queryFn:  regOps.sources.list as () => Promise<RegSource[]>,
  });
  const jurisdictionsQ = useApiQuery({
    queryKey: ['regops', 'jurisdictions'],
    queryFn:  regOps.jurisdictions.list as () => Promise<MedicalJurisdiction[]>,
  });

  const [creating, setCreating] = useState(false);

  const filtered = useMemo(() => {
    const all = sourcesQ.data ?? [];
    if (!filterJurisdictionId) return all;
    return all.filter((s) => s.jurisdiction === parseInt(filterJurisdictionId, 10));
  }, [sourcesQ.data, filterJurisdictionId]);

  const jurisdictionMap = useMemo(() => {
    const m = new Map<number, MedicalJurisdiction>();
    (jurisdictionsQ.data ?? []).forEach((j) => m.set(j.id, j));
    return m;
  }, [jurisdictionsQ.data]);

  return (
    <>
      <PageHeader
        title="Regulatory sources"
        subtitle="Statutes, regulations, guidelines, and case law each rule must cite."
        actions={
          <Button variant="contained" startIcon={<AddIcon />}
                  onClick={() => setCreating(true)}>
            New source
          </Button>
        }
      />

      <DataState
        query={sourcesQ}
        empty={{ title: 'No sources yet',
                 description: 'Every rule must cite at least one source. Add them here first.' }}
      >
        {() => filtered.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No sources for this filter.</Typography>
        ) : (
          <Stack spacing={1.5}>
            {filtered.map((s) => (
              <Row key={s.id} source={s}
                   jurisdictionCode={jurisdictionMap.get(s.jurisdiction)?.code ?? '?'} />
            ))}
          </Stack>
        )}
      </DataState>

      {creating && (
        <SourceDialog
          jurisdictions={jurisdictionsQ.data ?? []}
          onClose={() => setCreating(false)}
        />
      )}
    </>
  );
}

function Row({ source, jurisdictionCode }: { source: RegSource; jurisdictionCode: string }) {
  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            bgcolor: 'background.subtle', color: 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <GavelIcon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {source.title}
              </Typography>
              <Chip size="small" label={jurisdictionCode}
                    variant="outlined" className="tabular" />
              <Chip size="small" label={source.source_type.replace(/_/g, ' ')}
                    variant="outlined" sx={{ textTransform: 'capitalize' }} />
            </Stack>
            <Typography variant="caption" color="text.secondary" className="tabular">
              effective {formatDate(source.effective_from)}
              {source.effective_to && ` – ${formatDate(source.effective_to)}`}
              {source.source_hash && ` · hash ${source.source_hash.slice(0, 12)}`}
            </Typography>
            {source.notes && (
              <Typography variant="caption" color="text.secondary"
                          sx={{ display: 'block', mt: 0.5 }}>
                {source.notes}
              </Typography>
            )}
          </Box>
          {source.url && (
            <Button size="small" startIcon={<OpenInNewIcon />}
                    href={source.url} target="_blank" rel="noopener">
              Open
            </Button>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

function SourceDialog({
  jurisdictions, onClose,
}: { jurisdictions: MedicalJurisdiction[]; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();

  const [jurisdictionId, setJurisdictionId] = useState('');
  const [sourceType, setSourceType] = useState('statute');
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [effectiveTo, setEffectiveTo] = useState('');
  const [notes, setNotes] = useState('');

  const createMut = useApiMutation({
    mutationFn: () => regOps.sources.create({
      jurisdiction: parseInt(jurisdictionId, 10),
      source_type: sourceType,
      title, url,
      effective_from: effectiveFrom,
      effective_to: effectiveTo || null,
      notes,
    }),
    onSuccess: () => {
      toast.success('Source registered.');
      qc.invalidateQueries({ queryKey: ['regops', 'sources'] });
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  const valid = jurisdictionId && title.trim().length > 1 && effectiveFrom;

  return (
    <Dialog open onClose={() => !createMut.isPending && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>New regulatory source</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth select required label="Jurisdiction"
                       value={jurisdictionId}
                       onChange={(e) => setJurisdictionId(e.target.value)}>
              {jurisdictions.map((j) => (
                <MenuItem key={j.id} value={String(j.id)}>
                  {j.code} — {j.display_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth select label="Source type"
                       value={sourceType}
                       onChange={(e) => setSourceType(e.target.value)}>
              {SOURCE_TYPES.map((t) => (
                <MenuItem key={t.code} value={t.code}>{t.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth required label="Title"
                       value={title} onChange={(e) => setTitle(e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="URL"
                       value={url} onChange={(e) => setUrl(e.target.value)}
                       placeholder="https://eur-lex.europa.eu/..." />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth required type="date" label="Effective from"
                       value={effectiveFrom}
                       onChange={(e) => setEffectiveFrom(e.target.value)}
                       InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth type="date" label="Effective to"
                       value={effectiveTo}
                       onChange={(e) => setEffectiveTo(e.target.value)}
                       InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline minRows={2} label="Notes"
                       value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={createMut.isPending}>Cancel</Button>
        <Button variant="contained"
                disabled={!valid || createMut.isPending}
                onClick={() => createMut.mutate()}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
